import {call, put, takeEvery, debounce, select, delay} from 'redux-saga/effects';
import {
    PRELOAD_DATA,
    SET_COUNTRIES,
    SET_QUERY,
    SET_QUERY_RESULTS,
    PREVIEW,
    SET_MAP_PRINT_REVIEW_IMAGE_URL,
    SET_ERROR,
} from '../actions/generator';
import config from '../config';
import {getAnalyticsReferrerPolicy} from '../cookieConsent';

function* fetchCountriesSaga() {
    try {
        const response = yield call(fetch, `${config.apiHost}/map/api/countries`, {
            referrerPolicy: getAnalyticsReferrerPolicy(),
        });
        if (!response.ok) {
            throw new Error('API error!');
        }
        const data = yield response.json();
        yield put({type: SET_COUNTRIES, payload: data});
    } catch (e) {
        console.error(e);
        yield put({type: SET_ERROR, payload: 'errors.unavailable'});
    }
}

function* fetchQueryResults(action) {
    try {
        const query = action.payload;
        const country = yield select((state) => state.generator.country);

        if (!query || !country || !country.iso_alpha_2_code) {
            return;
        }

        const url = new URL('https://nominatim.openstreetmap.org/search.php');

        url.searchParams.set('city', query);
        url.searchParams.set('format', 'geojson');
        url.searchParams.set('namedetails', '1');
        url.searchParams.set('dedupe', '1');
        url.searchParams.set('countrycodes', country.iso_alpha_2_code.toLowerCase());
        url.searchParams.set('limit', '100');

        const response = yield call(fetch, url, {referrerPolicy: 'no-referrer'});

        if (!response.ok) {
            throw new Error('OSM API error!');
        }

        const data = yield response.json();
        const features = data.features.map((feature) => {
            const availableNames = feature.properties.namedetails || {name: feature.properties.name};

            let possibleNames = [availableNames['name'], availableNames['alt_name'], availableNames['old_name']];
            (country.language_codes || []).forEach((lang) => {
                possibleNames.push(availableNames[`name:${lang}`]);
                possibleNames.push(availableNames[`alt_name:${lang}`]);
                possibleNames.push(availableNames[`old_name:${lang}`]);
            });
            possibleNames.push(availableNames['name:en'], availableNames['int_name']);

            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    names: Array.from(new Set(possibleNames.filter(Boolean))),
                },
            };
        });

        const current = yield select((state) => state.generator);
        if (current.query === query && current.country?.iso_alpha_2_code === country.iso_alpha_2_code) {
            yield put({type: SET_QUERY_RESULTS, payload: features});
        }
    } catch (e) {
        console.error(e);
        yield put({type: SET_ERROR, payload: 'errors.unavailable'});
    }
}

export function* createMapRequest() {
    try {
        const state = yield select((state) => state.generator);
        const requestData = {
            utm: state.utmParams,
            client_referrer: state.referrer,
            country_id: state.country.iso_alpha_2_code,
            region_id: state.region ? state.region.id : null,
            background_color: state.color.main,
            text_color: state.color.text,
            full_image_width: state.isHorizontalLayout ? state.canvasHeight : state.canvasWidth,
            full_image_height: state.isHorizontalLayout ? state.canvasWidth : state.canvasHeight,
            selected_features: {type: 'FeatureCollection', features: state.selectedFeatures},
        };

        const response = yield call(fetch, `${config.apiHost}/map/api/map-requests`, {
            method: 'POST',
            referrerPolicy: getAnalyticsReferrerPolicy(),
            body: JSON.stringify(requestData),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('API error!');
        }

        const data = yield response.json();
        const mapRequestId = data.id;
        if (typeof mapRequestId !== 'string' || !mapRequestId.trim()) {
            throw new Error('API data error!');
        }

        yield call(waitForMapIsReady, mapRequestId);
    } catch (e) {
        console.error(e);
        yield put({type: SET_ERROR, payload: 'errors.unavailable'});
        yield put({
            type: SET_MAP_PRINT_REVIEW_IMAGE_URL,
            payload: {
                mapRequestId: null,
                mapPrintPreview: {
                    mapPrintPreviewImageUrl: null,
                    mapPrintPreviewFullImageUrl: null,
                },
            },
        });
    }
}

function* waitForMapIsReady(mapRequestId) {
    let isSuccess = false;
    let pollingTime = config.pollingTime;
    const sleepTime = config.pollingSleep;

    yield delay(config.waitBeforePolling * 1000);

    try {
        while (pollingTime >= 0) {
            const response = yield call(fetch, `${config.apiHost}/map/api/map-requests/${mapRequestId}`, {
                referrerPolicy: getAnalyticsReferrerPolicy(),
            });

            if (!response.ok) {
                throw new Error('API error!');
            }

            const data = yield response.json();

            if (typeof data.preview_image === 'string' && data.preview_image.trim()) {
                isSuccess = true;
                yield put({
                    type: SET_MAP_PRINT_REVIEW_IMAGE_URL,
                    payload: {
                        mapRequestId: mapRequestId,
                        mapPrintPreview: {
                            mapPrintPreviewImageUrl: data.preview_image,
                            mapPrintPreviewFullImageUrl: data.preview_image_full,
                        },
                    },
                });
                break;
            }

            yield delay(sleepTime * 1000);
            pollingTime -= sleepTime;
        }

        if (!isSuccess) {
            yield put({type: SET_ERROR, payload: 'errors.timeout'});
            yield put({
                type: SET_MAP_PRINT_REVIEW_IMAGE_URL,
                payload: {
                    mapRequestId: null,
                    mapPrintPreview: {
                        mapPrintPreviewImageUrl: null,
                        mapPrintPreviewFullImageUrl: null,
                    },
                },
            });
        }
    } catch (e) {
        console.error(e);
        yield put({type: SET_ERROR, payload: 'errors.unavailable'});
        yield put({
            type: SET_MAP_PRINT_REVIEW_IMAGE_URL,
            payload: {
                mapRequestId: null,
                mapPrintPreview: {
                    mapPrintPreviewImageUrl: null,
                    mapPrintPreviewFullImageUrl: null,
                },
            },
        });
    }
}

export function* watchPreloadData() {
    yield takeEvery(PRELOAD_DATA, fetchCountriesSaga);
}

export function* watchQuery() {
    yield debounce(500, SET_QUERY, fetchQueryResults);
}

export function* watchPreview() {
    yield takeEvery(PREVIEW, createMapRequest);
}
